// GV design system tokens (navy #1B2A4A family, gold #C9A227 family,
// Cormorant Garamond headings — loaded by the HOST page; we only declare the
// family with a graceful serif fallback and never fetch fonts ourselves).
// All styles live inside the shadow root; host CSS cannot reach in.

export const GV_NAVY = "#1B2A4A";
export const GV_NAVY_SOFT = "#2E4368";
export const GV_GOLD = "#C9A227";
export const GV_GOLD_DEEP = "#A8861D";

// Chart data palette — validated (dataviz six checks, light surface):
// two categorical fills; the net line is deep brand navy with a distinct
// mark form (line + markers) and a legend entry, never a third fill.
export const CHART_SERIES_A = "#3A5EA8"; // navy-family data blue
export const CHART_SERIES_B = "#A8861D"; // gold-family data gold
export const CHART_LINE = GV_NAVY;
export const CHART_GRID = "rgba(27, 42, 74, 0.10)";
export const CHART_TICK = "#5A6478";

export const FONT_HEADING =
  '"Cormorant Garamond", "Cormorant", Georgia, "Times New Roman", serif';
export const FONT_BODY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const STYLES = `
:host {
  all: initial;
  display: block;
  font-family: ${FONT_BODY};
  color: ${GV_NAVY};
}
* { box-sizing: border-box; }

.gvf-card {
  background: #ffffff;
  border: 1px solid rgba(27, 42, 74, 0.14);
  border-radius: 10px;
  padding: 20px 24px 24px;
  max-width: 960px;
  font-size: 14px;
  line-height: 1.45;
}

.gvf-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  border-bottom: 2px solid ${GV_GOLD};
  padding-bottom: 12px;
}
.gvf-title {
  font-family: ${FONT_HEADING};
  font-size: 26px;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 0;
  flex: 1 1 auto;
}
.gvf-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}
.gvf-period-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.gvf-period {
  font-family: ${FONT_BODY};
  font-size: 13px;
  color: ${GV_NAVY};
  border: 1px solid rgba(27, 42, 74, 0.3);
  border-radius: 6px;
  padding: 5px 8px;
  background: #fff;
}
.gvf-period-nav {
  font-family: ${FONT_BODY};
  font-size: 18px;
  line-height: 1;
  font-weight: 600;
  color: ${GV_NAVY};
  background: transparent;
  border: 1px solid rgba(27, 42, 74, 0.3);
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
}
.gvf-period-nav:hover { background: rgba(27, 42, 74, 0.06); }
.gvf-period-nav:focus-visible,
.gvf-period:focus-visible,
.gvf-binder-btn:focus-visible,
.gvf-tab:focus-visible,
.gvf-retry-btn:focus-visible {
  outline: 2px solid ${GV_GOLD};
  outline-offset: 2px;
}
.gvf-binder-btn {
  font-family: ${FONT_BODY};
  font-size: 13px;
  font-weight: 600;
  color: ${GV_NAVY};
  background: transparent;
  border: 1px solid ${GV_GOLD};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
}
.gvf-binder-btn:hover { background: rgba(201, 162, 39, 0.12); }
.gvf-binder-btn:disabled { opacity: 0.5; cursor: default; }
.gvf-binder-note { font-size: 12px; color: ${CHART_TICK}; }

.gvf-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin: 14px 0 18px;
  border-bottom: 1px solid rgba(27, 42, 74, 0.14);
}
.gvf-tab {
  font-family: ${FONT_BODY};
  font-size: 13px;
  font-weight: 600;
  color: ${GV_NAVY_SOFT};
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 8px 14px;
  cursor: pointer;
}
.gvf-tab:hover { color: ${GV_NAVY}; }
.gvf-tab[aria-selected="true"] {
  color: ${GV_NAVY};
  border-bottom-color: ${GV_GOLD};
}

.gvf-section-title {
  font-family: ${FONT_HEADING};
  font-size: 20px;
  font-weight: 600;
  margin: 18px 0 8px;
}
.gvf-meta { font-size: 12px; color: ${CHART_TICK}; margin: 2px 0 0; }

.gvf-chart-block { margin: 16px 0 8px; }
.gvf-chart-wrap { position: relative; height: 260px; }
.gvf-chart-wrap canvas { max-width: 100%; }

table.gvf-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0 4px;
  font-variant-numeric: tabular-nums;
}
.gvf-table caption {
  text-align: left;
  font-family: ${FONT_HEADING};
  font-size: 17px;
  font-weight: 600;
  padding: 6px 0;
}
.gvf-table th {
  text-align: left;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${GV_NAVY_SOFT};
  border-bottom: 1px solid rgba(27, 42, 74, 0.25);
  padding: 6px 8px;
}
.gvf-table td {
  padding: 6px 8px;
  border-bottom: 1px solid rgba(27, 42, 74, 0.08);
}
.gvf-table th.gvf-num, .gvf-table td.gvf-num { text-align: right; }
.gvf-table tr.gvf-total td {
  font-weight: 700;
  border-top: 2px solid ${GV_NAVY};
  border-bottom: none;
}
.gvf-neg { color: #7a3030; }
.gvf-account { color: ${CHART_TICK}; font-size: 12px; }

.gvf-headline {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 14px 0 4px;
}
.gvf-headline-label {
  font-family: ${FONT_HEADING};
  font-size: 18px;
  font-weight: 600;
}
.gvf-headline-value {
  font-size: 24px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.gvf-headline-rule {
  flex: 1;
  border-bottom: 2px solid ${GV_GOLD};
  transform: translateY(-6px);
}

.gvf-state {
  padding: 28px 12px;
  text-align: center;
  color: ${GV_NAVY_SOFT};
  font-size: 14px;
}
.gvf-state.gvf-error { color: #7a3030; }
.gvf-state .gvf-state-title {
  font-family: ${FONT_HEADING};
  font-size: 20px;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  color: ${GV_NAVY};
}
.gvf-retry-btn {
  display: inline-block;
  margin-top: 12px;
  font-family: ${FONT_BODY};
  font-size: 13px;
  font-weight: 600;
  color: ${GV_NAVY};
  background: transparent;
  border: 1px solid ${GV_GOLD};
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
}
.gvf-retry-btn:hover { background: rgba(201, 162, 39, 0.12); }

.gvf-footer {
  margin-top: 16px;
  font-size: 11px;
  color: ${CHART_TICK};
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
`;
